// Deal Chatbot Service
// Handles cache preloading and chatbot-related functionality

/**
 * Trigger deal cache preloading in the backend
 * This will preload common queries for instant responses
 * @param dealId - The deal ID to preload
 * @param userId - The user ID
 * @returns Promise<boolean> - true if successful
 */
export const triggerDealCachePreload = async (dealId: string, userId: string): Promise<boolean> => {
  try {
    const response = await fetch('/api/deals/preload-cache', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ dealId, userId })
    });
    return response.ok;
  } catch (error) {
    console.error('Failed to trigger cache preload:', error);
    return false;
  }
};

/**
 * Check if deal cache is preloaded
 * @param dealId - The deal ID
 * @param userId - The user ID
 * @returns Promise<boolean> - true if cache is ready
 */
export const checkDealCacheStatus = async (dealId: string, userId: string): Promise<boolean> => {
  try {
    const response = await fetch(`/api/deals/cache-status/${dealId}/${userId}`);
    if (response.ok) {
      const data = await response.json();
      return data.preloaded || false;
    }
    return false;
  } catch (error) {
    console.error('Failed to check cache status:', error);
    return false;
  }
};

/**
 * Get suggested prompts for a deal stage
 * @param dealStage - The current deal stage
 * @returns Array of suggested prompts
 */
export const getSuggestedPrompts = (dealStage: string): string[] => {
  const stagePrompts: { [key: string]: string[] } = {
    under_contract: [
      "What are the upcoming deadlines?",
      "When is the inspection period ending?",
      "Schedule inspection tasks"
    ],
    pre_closing: [
      "When is the closing date?",
      "Show closing checklist",
      "Are all documents ready?"
    ],
    closing: [
      "Final walkthrough checklist",
      "Confirm closing location",
      "Verify funds are ready"
    ]
  };
  
  return stagePrompts[dealStage] || [
    "What's the current status of this deal?",
    "Who are the parties involved?",
    "What tasks need attention?"
  ];
};